import { isAdderCompatibleWithNsp } from './nonSolarAdderRules'
import { QuoteAdderRow, QUOTE_ADDER_STUDIO_DEFAULTS } from './types'

export type AdderLibraryRecord = {
  id: number
  url?: string
  title: string
  type?: number
  value?: number
  cost_value?: number
  tax_included?: number
  category?: number | null
  formula?: number
  show_customer?: boolean
  value_includes_tax?: boolean
  unit_label?: string | null
  dealer_fee_interaction_type?: number
  price_treatment?: number
  show_customer_cost?: boolean
  display_as_line_item?: boolean
  is_archived?: boolean
}

/**
 * Map the integer ``Adder.formula`` column to the canonical Studio string form
 * the pricing pipeline expects. ``Adder.formula`` is an ``IntegerField`` so the
 * ``/adders/`` list serialises it as a number, but the calc pipeline
 * (``pricing_formula.calculate_adders_by_formula_type`` server-side, mirrored by
 * Studio) only recognises the string keys — any other value is treated as
 * legacy / unknown and the row is silently dropped from ``pricing.line_items``.
 * Without this conversion a library adder would display in the table but never
 * affect the recalc or the persisted price. Mirrors
 * ``_ADDER_FORMULA_INT_TO_STR`` in ``django/osapi/project/quote_system.py`` and
 * ``PRICE_ONLY_ADDER_FORMULA_FROM_DATABASE`` in ``Pricing.jsx``.
 */
const ADDER_FORMULA_INT_TO_STRING: Record<number, string> = {
  0: 'fixed',
  1: 'perWatt',
  2: 'perPanel',
  3: 'priceInclTax',
  4: 'priceExclTax',
  5: 'pricePayableInclTax',
  6: 'pricePayableExclTax',
}

export const normaliseAdderFormulaToStudioString = (
  formula: number | string | null | undefined
): string => {
  if (typeof formula === 'string' && formula) return formula
  if (typeof formula === 'number') return ADDER_FORMULA_INT_TO_STRING[formula] ?? 'fixed'
  return 'fixed'
}

export const filterLibraryAddersForNsp = <T extends object>(records: T[], isNsp: boolean): T[] =>
  isNsp ? records.filter(isAdderCompatibleWithNsp) : records

export const excludeAlreadySelectedLibraryRows = <T extends { id: number }>(
  records: T[],
  existingLibraryIds: readonly number[]
): T[] => {
  if (!existingLibraryIds.length) return records
  const blocked = new Set(existingLibraryIds)
  return records.filter((record) => !blocked.has(record.id))
}

export const mapAdderLibraryRecordToQuoteRow = (
  record: AdderLibraryRecord,
  defaultTaxRate: number
): QuoteAdderRow => ({
  ...QUOTE_ADDER_STUDIO_DEFAULTS,
  adder_id: record.id,
  label: record.title,
  quantity: 1,
  value: record.value ?? 0,
  tax_included: record.tax_included ?? defaultTaxRate,
  category: record.category ?? QUOTE_ADDER_STUDIO_DEFAULTS.category,
  type: record.type ?? QUOTE_ADDER_STUDIO_DEFAULTS.type,
  cost_value: record.cost_value ?? QUOTE_ADDER_STUDIO_DEFAULTS.cost_value,
  formula: normaliseAdderFormulaToStudioString(record.formula),
  show_customer: record.show_customer ?? QUOTE_ADDER_STUDIO_DEFAULTS.show_customer,
  value_includes_tax: true,
  unit_label: record.unit_label ?? QUOTE_ADDER_STUDIO_DEFAULTS.unit_label,
  dealer_fee_interaction_type:
    record.dealer_fee_interaction_type ?? QUOTE_ADDER_STUDIO_DEFAULTS.dealer_fee_interaction_type,
  price_treatment: record.price_treatment ?? QUOTE_ADDER_STUDIO_DEFAULTS.price_treatment,
  show_customer_cost: record.show_customer_cost ?? QUOTE_ADDER_STUDIO_DEFAULTS.show_customer_cost,
  display_as_line_item: record.display_as_line_item ?? QUOTE_ADDER_STUDIO_DEFAULTS.display_as_line_item,
})

/** Locked field values for ad-hoc adders on quick-quote (NSP) projects. */
export const NSP_MANUAL_ADDER_LOCKED_FIELDS = {
  type: 0,
  formula: 'fixed' as const,
  show_customer: true,
  display_as_line_item: true,
  value_includes_tax: true,
}

export const buildManualQuoteAdderDefaults = (defaultTaxRate: number): Partial<QuoteAdderRow> => ({
  ...QUOTE_ADDER_STUDIO_DEFAULTS,
  ...NSP_MANUAL_ADDER_LOCKED_FIELDS,
  unit_label: '',
  category: null,
  tax_included: defaultTaxRate,
})
